import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, Globe, ArrowRight, Loader2 } from 'lucide-react';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('commercial');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error(t('required'));
      return;
    }

    setLoading(true);
    try {
      const response = await register(email, password, name, role);
      
      // Check if registration requires approval (new security flow)
      if (response?.status === 'pending_approval') {
        toast.success(
          language === 'fr' 
            ? 'Inscription réussie ! En attente de validation par l\'administrateur.' 
            : 'Registration successful! Awaiting admin approval.',
          { duration: 6000 }
        );
        // Redirect to login with a message instead of dashboard
        navigate('/login', { state: { pendingApproval: true } });
      } else {
        toast.success(t('welcome') + '!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1759153820384-12c9ddf8bd8d?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/wasabi-logo.png" alt="Wasabi" className="h-14 w-auto" />
          </div>
          
          <div className="space-y-6">
            <h1 className="font-heading font-black text-5xl leading-tight">
              Rejoignez<br />la plateforme<br />CAPEX Wasabi
            </h1>
            <p className="text-xl text-slate-300 max-w-md">
              Créez votre compte et commencez à analyser vos projets d'investissement dès maintenant.
            </p>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-300">
            <span>3 hypothèses</span>
            <span className="w-1 h-1 rounded-full bg-orange-400" />
            <span>P&L 4 ans</span>
            <span className="w-1 h-1 rounded-full bg-orange-400" />
            <span>Export PDF</span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50 dark:bg-slate-900">
        <div className="w-full max-w-md space-y-8">
          {/* Logo CAPEX Wasabi Business */}
          <div className="flex items-center justify-center" data-testid="register-brand-logo">
            <div className="rounded-2xl bg-[#09090B] px-8 py-6 shadow-lg ring-1 ring-black/5 w-full">
              <img src="/capex-logo-black.webp" alt="CAPEX — Wasabi Business" className="h-20 lg:h-24 w-full object-contain select-none" draggable={false} />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="gap-2"
            >
              <Globe className="w-4 h-4" />
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>
          </div>

          <Card className="border-0 shadow-xl" data-testid="register-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-heading">{t('register')}</CardTitle>
              <CardDescription>
                {t('hasAccount')}{' '}
                <Link to="/login" className="text-blue-600 hover:underline font-medium">
                  {t('signIn')}
                </Link>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('name')}</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 pr-12"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">{t('role')}</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="h-12" data-testid="register-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dg">DG</SelectItem>
                      <SelectItem value="dop">DOP</SelectItem>
                      <SelectItem value="directeur_commercial">Directeur Commercial</SelectItem>
                      <SelectItem value="directeur_regional">Directeur Régional</SelectItem>
                      <SelectItem value="directrice_regionale">Directrice Régionale</SelectItem>
                      <SelectItem value="business_manager">Business Manager</SelectItem>
                      <SelectItem value="commercial">Commercial(e)</SelectItem>
                      <SelectItem value="assistante_commerciale">Assistante Commerciale</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 gap-2 text-base" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {t('signUp')}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            Données sécurisées et hébergées en Europe
          </p>
        </div>
      </div>
    </div>
  );
}
